import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { buildMyClassesQuery, getDocsByClassIds, schoolYearQuarterLabels } from '../utils/classroomQueries';
import { usePageLoad } from '../hooks/usePageLoad';
import LoadErrorNotice from '../components/LoadErrorNotice';
import { lastNameComparator } from '../utils/sortByLastName';
import { isFeatureEnabled } from '../constants/featureFlags';
import { TEST_TYPE_LABELS } from '../constants/assessmentTypes';
import { formatLetterForAssessment, letterCaseForSelection } from '../utils/letterCase';
import { VOWEL_TEST_LABELS, isVowelTestType, isVowelItemKey, vowelItemLabel, enabledVowelTestTypes } from '../constants/vowelTests';
import { isDocInSchoolYear, isQuarterInSchoolYear } from '../utils/schoolYear';
import { resolveActiveSchoolYear } from '../utils/activeSchoolYear';
import SchoolYearNotice from '../components/SchoolYearNotice';
import { buildClassLabelMap, classLabelFrom } from '../utils/classDisplay';

// ─── Pull-out groups from TLS (read-only on teacher side) ────────────────────

// Focus letters render in the group's own skill case (lowercase groups show
// lowercase glyphs). See utils/letterCase.js.
const formatPullOutItem = (item, testType) => formatLetterForAssessment(testType, item);

function PullOutGroupsSection({ user }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'smallGroups'),
          where('teacherIds', 'array-contains', user.uid),
        ));
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Only show groups that actually involve this teacher's students.
        // (teacherIds is denormalized but a defensive client-side filter
        //  keeps stale data from leaking through.)
        const filtered = rows.map((g) => ({
          ...g,
          myStudents: (g.students || []).filter((s) => s.teacherId === user.uid),
        })).filter((g) => g.myStudents.length > 0);
        // Most recent first (createdAt is a Firestore timestamp).
        filtered.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || 0;
          const tb = b.createdAt?.toMillis?.() || 0;
          return tb - ta;
        });
        setGroups(filtered);
      } catch (e) {
        console.error('Failed to load pull-out groups:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.uid]);

  if (loading || groups.length === 0) return null;

  return (
    <div style={{ marginBottom: 28, padding: '16px 18px', background: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: 12 }} className="no-print">
      <div style={{ fontSize: '0.78em', color: '#0c4a6e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Pull-out groups from your TLS
      </div>
      {groups.map((g) => (
        <div key={g.id} style={{ background: '#fff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>{g.name}</div>
            <div style={{ fontSize: '0.8em', color: '#64748b' }}>From {g.ownerName || 'your TLS'}</div>
          </div>
          <div style={{ fontSize: '0.86em', color: '#475569', marginBottom: 6 }}>
            Your students in this group:{' '}
            <strong>{g.myStudents.map((s) => s.name).join(', ')}</strong>
          </div>
          <div style={{ fontSize: '0.82em', color: '#64748b', marginBottom: g.notes ? 6 : 0 }}>
            Focus: {(g.focusItems || []).map((i) => formatPullOutItem(i, g.focusTestType)).join(', ')}
            {' '}({TEST_TYPE_LABELS[g.focusTestType] || g.focusTestType}) · {g.quarter}
          </div>
          {g.notes && (
            <div style={{ background: '#f8fafc', borderLeft: '3px solid #0284c7', borderRadius: 4, padding: '8px 12px', fontSize: '0.85em', color: '#475569', marginTop: 6 }}>
              <strong style={{ color: '#0284c7' }}>Note:</strong> {g.notes}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Algorithm ────────────────────────────────────────────────────────────────

function buildWeakLetters(studentId, assessments, testFocus, dataRange, cumulativeThrough) {
  let pool = assessments.filter(a =>
    a.studentId === studentId &&
    a.correctCount !== -1 &&
    a.responses &&
    Object.keys(a.responses).length > 0
  );

  const types = testFocus === 'all' ? ['uppercase', 'lowercase', 'sounds'] : [testFocus];
  pool = pool.filter(a => types.includes(a.testType));

  if (dataRange === 'latest') {
    // Pick the most recent attempt per testType. Tiebreak on timestamp when
    // quarter strings are equal — without this, multiple same-quarter retakes
    // fall back to Firestore document order (typically creation order =
    // earliest), so any improvement on a re-assessment is silently ignored.
    const ts = a => a?.timestamp?.getTime?.() ?? 0;
    const latestByType = {};
    pool.forEach(a => {
      const cur = latestByType[a.testType];
      if (!cur
          || a.quarter > cur.quarter
          || (a.quarter === cur.quarter && ts(a) > ts(cur))) {
        latestByType[a.testType] = a;
      }
    });
    pool = Object.values(latestByType);
  } else if (dataRange === 'cumulative' && cumulativeThrough) {
    pool = pool.filter(a => a.quarter <= cumulativeThrough);
  }

  const weak = new Set();
  pool.forEach(a => {
    Object.entries(a.responses).forEach(([letter, result]) => {
      if (result === 'Incorrect') weak.add(letter.toLowerCase());
    });
  });
  return weak;
}

function findFocusLetters(members, max = 5) {
  const counts = {};
  members.forEach(s => s.weakLetters.forEach(l => { counts[l] = (counts[l] || 0) + 1; }));
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, max)
    .map(([l]) => l);
}

function generateGroups(students, assessments, testFocus, dataRange, cumulativeThrough) {
  const profiles = students
    .map(s => ({
      id: s.id,
      name: s.name,
      weakLetters: buildWeakLetters(s.id, assessments, testFocus, dataRange, cumulativeThrough),
    }))
    .filter(s => s.weakLetters.size > 0);

  if (profiles.length === 0) return [];

  const remaining = [...profiles];
  const groups = [];

  while (remaining.length > 0) {
    if (remaining.length <= 5) {
      groups.push({ students: [...remaining], focusLetters: findFocusLetters(remaining), focusTestType: testFocus });
      break;
    }

    remaining.sort((a, b) => b.weakLetters.size - a.weakLetters.size);
    const anchor = remaining[0];

    const scored = remaining.slice(1)
      .map(s => ({ ...s, overlap: [...anchor.weakLetters].filter(l => s.weakLetters.has(l)).length }))
      .sort((a, b) => b.overlap - a.overlap);

    const members = [anchor, ...scored.slice(0, Math.min(4, scored.length))];
    groups.push({ students: members, focusLetters: findFocusLetters(members), focusTestType: testFocus });

    const ids = new Set(members.map(s => s.id));
    remaining.splice(0, remaining.length, ...remaining.filter(s => !ids.has(s.id)));
  }

  return groups;
}

// ─── Plain-English descriptions ───────────────────────────────────────────────

function getGroupDescription(focusLetters) {
  // Vowel-test items ('a_long' / 'a_short') pattern-match on their base
  // vowel, so a vowel-focused group gets the existing vowel tip.
  const s = new Set(focusLetters.map(l => (isVowelItemKey(l) ? l.charAt(0) : l)));
  const count = (arr) => arr.filter(l => s.has(l)).length;

  if (count(['b', 'd', 'p', 'q']) >= 2)
    return "These students mix up letters that look like mirror images of each other. Letter formation with physical objects, tactile tracing, or a \"b vs. d\" anchor chart work well here.";
  if (count(['m', 'n', 'u', 'w', 'h']) >= 2)
    return "These students confuse letters with similar strokes. Counting humps or legs and using color-coded charts can help them tell these apart.";
  if (count(['a', 'e', 'i', 'o', 'u']) >= 2)
    return "These students need more practice with vowels. Short vowel sound sorting, letter-picture matching, and vowel-focused word families are good starting points.";
  if (count(['c', 'o', 'e', 'a', 'g']) >= 2)
    return "These students mix up rounded letters. Tracing curved shapes and comparing them side by side can build the visual distinction they need.";
  if (['b', 'f', 'h', 'k', 'l', 't'].some(l => s.has(l)) && ['g', 'j', 'p', 'q', 'y'].some(l => s.has(l)))
    return "These students mix up letters that sit differently on the line. Using lined paper and talking about where letters live — sky, grass, underground — can make a big difference.";
  return "These students share similar letter recognition gaps. Focused letter card games, whiteboard practice, or hands-on letter sorting would help this group.";
}

// Display form of a weak item for a group's focus: letters render in the
// focus skill's case (unchanged); vowel-test items ('a_long') render as
// "a — long" (v3.73.0).
const formatWeakItem = (focusTestType, item) =>
  isVowelTestType(focusTestType) || isVowelItemKey(item)
    ? vowelItemLabel(item)
    : formatLetterForAssessment(letterCaseForSelection(focusTestType), item);

// ─── Group Card ───────────────────────────────────────────────────────────────

const CARD_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

function GroupCard({ group, index }) {
  const color = CARD_COLORS[index % CARD_COLORS.length];
  return (
    <div style={{ border: `2px solid ${color}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '16px', backgroundColor: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontWeight: 700, fontSize: '1.05em', color }}>Group {index + 1}</span>
        <span style={{ fontSize: '0.8em', color: '#64748b' }}>{group.students.length} student{group.students.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ marginBottom: '14px' }}>
        {(() => {
          const focusSet = new Set(group.focusLetters);
          return group.students.map(s => {
            const sortedLetters = [...s.weakLetters].sort();
            return (
              <div key={s.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', padding: '5px 0', borderBottom: '1px dashed #f1f5f9', gap: '8px' }}>
                <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.92em', minWidth: '160px' }}>{s.name}</span>
                <span style={{ fontSize: '0.85em', color: '#94a3b8' }}>
                  missed {sortedLetters.length}:{' '}
                  {sortedLetters.map((l, i) => (
                    <span key={l} style={{ fontWeight: focusSet.has(l) ? 700 : 400, color: focusSet.has(l) ? '#334155' : '#94a3b8' }}>
                      {i > 0 ? ', ' : ''}{formatWeakItem(group.focusTestType, l)}
                    </span>
                  ))}
                </span>
              </div>
            );
          });
        })()}
      </div>

      <div style={{ fontSize: '0.78em', color: '#64748b', marginBottom: '6px', fontStyle: 'italic' }}>
        The focus letters below are the letters most students in this group missed. Each student also has personal gaps (shown above) — bolded letters appear in both.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78em', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Focus letters:</span>
        {group.focusLetters.map(l => (
          <span key={l} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '34px', height: '34px', padding: isVowelItemKey(l) ? '0 10px' : 0, borderRadius: '6px', backgroundColor: color, color: '#fff', fontWeight: 700, fontSize: '1.05em', whiteSpace: 'nowrap' }}>
            {formatWeakItem(group.focusTestType, l)}
          </span>
        ))}
      </div>

      <div style={{ backgroundColor: '#f8fafc', borderRadius: '6px', padding: '10px 14px', fontSize: '0.85em', color: '#475569', borderLeft: `3px solid ${color}` }}>
        {getGroupDescription(group.focusLetters)}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function Groups({ user }) {
  const [classes, setClasses] = useState({});
  const [students, setStudents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [schoolYearInfo, setSchoolYearInfo] = useState(null);
  // The resolved active school-year start year, readable inside fetch closures.
  const activeYearRef = React.useRef(null);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [testFocus, setTestFocus] = useState('all');
  const [dataRange, setDataRange] = useState('latest');
  const [cumulativeThrough, setCumulativeThrough] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [groups, setGroups] = useState(null);
  const [noWeaknessStudents, setNoWeaknessStudents] = useState([]);

  const fetchData = useCallback(async () => {
    {
      // Which school year is active? Calendar-driven (district-specific first).
      const sy = await resolveActiveSchoolYear(user?.profile?.districtId);
      setSchoolYearInfo(sy);
      activeYearRef.current = sy.status === 'active' ? sy.startYear : null;
      if (sy.status !== 'active') return;

      // Role-aware query: pure-EA tokens use the assistantEmails clause only
      // (the or() shape is rules-denied without the teacher claim).
      const classesSnap = await getDocs(buildMyClassesQuery(user));
      const classesData = {};
      const classIds = [];
      classesSnap.docs.filter(d => !d.data().deleted && isDocInSchoolYear(d.data(), activeYearRef.current)).forEach(d => {
        classesData[d.id] = d.data();
        classIds.push(d.id);
      });
      setClasses(classesData);

      // Assessments scoped to the active year's quarters SERVER-SIDE — the
      // class's prior-year history is no longer downloaded (audit step 1b).
      // Students and assessments are independent, so they go out together
      // rather than one after the other (2026-07 perf audit, step 5).
      const [studentDocs, assessmentDocs] = await Promise.all([
        getDocsByClassIds('students', classIds),
        getDocsByClassIds('assessments', classIds, {
          quarterLabels: schoolYearQuarterLabels(activeYearRef.current),
        }),
      ]);

      const studentsList = studentDocs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => isDocInSchoolYear(s, activeYearRef.current));

      let assessmentsList = assessmentDocs.map(d => {
        const data = d.data();
        if (data.timestamp?.toDate) data.timestamp = data.timestamp.toDate();
        return { id: d.id, ...data };
      });

      assessmentsList = assessmentsList.filter(a => a.quarter?.includes('Q') && isQuarterInSchoolYear(a.quarter, activeYearRef.current));
      studentsList.sort(lastNameComparator);
      setStudents(studentsList);
      setAssessments(assessmentsList);

      if (classIds.length > 0) setSelectedClassId(classIds[0]);
    }
  }, [user]);

  // Shared load state: rules denials / missing indexes / offline surface as a
  // retryable notice instead of a silent empty page; hung loads error at 15s.
  const { loading, error: loadError, retry: retryLoad } = usePageLoad(fetchData, [fetchData], { enabled: !!user });

  useEffect(() => {
    document.title = 'Early Literacy Assessment - Small Groups';
  }, []);

  const classStudents = students.filter(s => s.classId === selectedClassId);
  const classAssessments = assessments.filter(a => a.classId === selectedClassId);
  const availableQuarters = [...new Set(classAssessments.map(a => a.quarter))].sort();
  const classKeys = Object.keys(classes);

  const runGenerate = (opts = {}) => {
    const focus = opts.testFocus ?? testFocus;
    const range = opts.dataRange ?? dataRange;
    const through = opts.cumulativeThrough ?? cumulativeThrough;
    const generated = generateGroups(classStudents, classAssessments, focus, range, through);
    const groupedIds = new Set(generated.flatMap(g => g.students.map(s => s.id)));
    setNoWeaknessStudents(classStudents.filter(s => !groupedIds.has(s.id)));
    setGroups(generated);
  };

  const handleQuickGenerate = () => {
    setTestFocus('all');
    setDataRange('latest');
    setCumulativeThrough('');
    runGenerate({ testFocus: 'all', dataRange: 'latest', cumulativeThrough: '' });
  };

  // Calendar-driven school-year gate (v3.29.0): when no school year is active
  // per the Academic Calendar, show the notice and no page content.
  if (!loading && schoolYearInfo && schoolYearInfo.status !== 'active') {
    return <SchoolYearNotice info={schoolYearInfo} />;
  }

  // Display labels (v3.71.0): override → shortened Clever name → raw.
  const classLabels = buildClassLabelMap(classes);

  return (
    <div style={{ padding: '20px', maxWidth: '860px', margin: '0 auto' }}>
      <div className="assessment-banner no-print">Letter/Sound Assessment</div>
      <h2 style={{ textAlign: 'center', marginBottom: '6px' }}>Small Groups</h2>
      <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9em', marginTop: 0, marginBottom: '24px' }}>
        Groups students by shared letter weaknesses so you know exactly who to work with and what to focus on.
      </p>

      {loading ? <p style={{ textAlign: 'center' }}>Loading...</p> : loadError ? (
        <LoadErrorNotice error={loadError} onRetry={retryLoad} />
      ) : (
        <>
          {isFeatureEnabled('tlsRole') && <PullOutGroupsSection user={user} />}

          {/* Class selector — only if multiple classes */}
          {classKeys.length > 1 && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }} className="no-print">
              <label style={{ fontWeight: 600, marginRight: '10px', color: '#475569' }}>Class:</label>
              <select
                value={selectedClassId}
                onChange={e => { setSelectedClassId(e.target.value); setGroups(null); }}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1em', minWidth: '200px' }}
              >
                {classKeys.map(id => (
                  <option key={id} value={id}>{classLabelFrom(classLabels, id)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Quick generate button */}
          <div style={{ textAlign: 'center', marginBottom: '12px' }} className="no-print">
            <button
              onClick={handleQuickGenerate}
              disabled={!selectedClassId}
              style={{ padding: '14px 36px', fontSize: '1.1em', fontWeight: 700, backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,0.3)', opacity: selectedClassId ? 1 : 0.5 }}
            >
              Just show me the groups
            </button>
          </div>

          {/* Advanced options */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }} className="no-print">
            <button
              onClick={() => setShowAdvanced(p => !p)}
              style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.88em', fontWeight: 600 }}
            >
              {showAdvanced ? '▲ Hide options' : '▼ Advanced options'}
            </button>

            {showAdvanced && (
              <div style={{ marginTop: '14px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'inline-flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', textAlign: 'left' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82em', marginBottom: '6px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Group students by the letters they're missing in...
                  </label>
                  <select
                    value={testFocus}
                    onChange={e => setTestFocus(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95em', minWidth: '260px' }}
                  >
                    <option value="all">All tests (Uppercase + Lowercase + Sounds)</option>
                    <option value="uppercase">Uppercase letters only</option>
                    <option value="lowercase">Lowercase letters only</option>
                    <option value="sounds">Letter sounds only</option>
                    {/* Optional vowel tests (v3.73.0): grouping bases of their own, like
                        Sounds. Deliberately NOT folded into "All tests" — vowel items
                        are sounds, not letter gaps. Absent when the kill switch is off. */}
                    {enabledVowelTestTypes().map(vt => (
                      <option key={vt} value={vt}>{VOWEL_TEST_LABELS[vt]} only</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82em', marginBottom: '6px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Based on...
                  </label>
                  <select
                    value={dataRange}
                    onChange={e => { setDataRange(e.target.value); if (e.target.value === 'latest') setCumulativeThrough(''); }}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95em', minWidth: '260px' }}
                  >
                    <option value="latest">Letters they're missing right now</option>
                    <option value="cumulative">All letters they've missed so far</option>
                  </select>
                  {dataRange === 'cumulative' && (
                    <select
                      value={cumulativeThrough}
                      onChange={e => setCumulativeThrough(e.target.value)}
                      style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95em', display: 'block', width: '100%' }}
                    >
                      <option value="">Through all quarters</option>
                      {availableQuarters.map(q => (
                        <option key={q} value={q}>Through {q}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => runGenerate()}
                    style={{ padding: '10px 24px', fontWeight: 700, backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.95em' }}
                  >
                    Generate Groups
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* "How are these groups created?" explainer — always available */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }} className="no-print">
            <button
              type="button"
              onClick={() => setShowHowItWorks(p => !p)}
              style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.88em', fontWeight: 600 }}
            >
              {showHowItWorks ? '▲ Hide explanation' : 'ⓘ How are these groups created?'}
            </button>

            {showHowItWorks && (
              <div style={{ marginTop: '14px', padding: '20px 24px', backgroundColor: '#fefce8', borderRadius: '10px', border: '1px solid #fde68a', textAlign: 'left', maxWidth: '720px', margin: '14px auto 0', color: '#334155', fontSize: '0.92em', lineHeight: 1.55 }}>
                <p style={{ marginTop: 0 }}>
                  We look at every letter your students missed in their Letter/Sound assessments and group students who missed similar letters together. The whole thing runs on your data right in the browser — no AI, no guessing.
                </p>
                <ol style={{ paddingLeft: '20px', marginBottom: '10px' }}>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Find the gaps.</strong> For each student, we list every letter they marked <em>Incorrect</em> across the test types you selected (Uppercase, Lowercase, and/or Sounds). N/A scores are skipped. Uppercase and lowercase versions of a letter count as the same gap.
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Pick an anchor.</strong> We start with the student who has the most weak letters, then add up to four more students whose weak letters overlap most with that anchor's. That's one group.
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Repeat.</strong> Pull those students out of the pool and run the same step again with the rest, until everyone is grouped. Each group is at most 5 students.
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Highlight focus letters.</strong> On each group card we show the top 5 letters that the most students in that group missed — those are the letters to focus on during small-group instruction.
                  </li>
                </ol>
                <p style={{ marginBottom: '10px' }}>
                  Students with no missed letters appear in the <strong>"On track"</strong> section, not in any group.
                </p>
                <p style={{ marginBottom: 0 }}>
                  The teaching tip on each card is matched to the group's focus letters (reversal pairs like b/d/p/q, vowels, rounded letters, and so on). It's a starting point — your knowledge of your students always wins.
                </p>
              </div>
            )}
          </div>

          {/* Group cards */}
          {groups !== null && (
            <div>
              {groups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', backgroundColor: '#f0fdf4', borderRadius: '10px', border: '2px solid #86efac' }}>
                  <div style={{ fontSize: '2.5em', marginBottom: '10px' }}>🎉</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1em', color: '#166534', marginBottom: '6px' }}>No letter gaps detected!</div>
                  <div style={{ color: '#4ade80', fontSize: '0.9em' }}>All students in this class appear to be on track based on the selected data.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, color: '#334155' }}>
                      {groups.length} Group{groups.length !== 1 ? 's' : ''} &mdash; {classLabelFrom(classLabels, selectedClassId)}
                    </h3>
                    <button
                      onClick={() => window.print()}
                      className="no-print"
                      style={{ padding: '6px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.85em', color: '#475569' }}
                    >
                      Print
                    </button>
                  </div>

                  {groups.map((group, i) => <GroupCard key={i} group={group} index={i} />)}

                  {noWeaknessStudents.length > 0 && (
                    <div style={{ border: '2px dashed #cbd5e1', borderRadius: '10px', padding: '18px 20px', color: '#64748b', backgroundColor: '#f8fafc', marginTop: '8px' }}>
                      <div style={{ fontWeight: 700, marginBottom: '8px', color: '#475569' }}>On track — no letter gaps detected</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {noWeaknessStudents.map(s => (
                          <span key={s.id} style={{ backgroundColor: '#e2e8f0', borderRadius: '20px', padding: '3px 12px', fontSize: '0.88em', fontWeight: 500 }}>
                            {s.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Groups;

// Named exports for unit tests (Groups.test.js). Pure functions, no React.
export { buildWeakLetters, findFocusLetters, generateGroups, getGroupDescription };
